import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, Optional, Self } from '@angular/core';
import { AbstractControl, FormControlDirective, NgControl } from '@angular/forms';

@Directive({ selector: '[fhPercentage]' })
export class PercentageDirective implements AfterViewInit, OnDestroy {

  get control(): AbstractControl | null {
    return (this.ngControl as FormControlDirective)?.form ?? this.ngControl?.control ?? null;
  }

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    @Optional() @Self() private readonly ngControl: NgControl
  ) {}

  ngAfterViewInit(): void {
    // Defer one tick so one-input's internal <input> is in the DOM
    // (this element is rendered via *ngIf, so the child component view
    // may not be fully initialized when ngAfterViewInit fires)
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        const input = this.nativeInput;
        if (!input) return;
        input.addEventListener('focus', this.onFocus);
        input.addEventListener('blur', this.onBlur);
        input.addEventListener('keydown', this.onKeydown);
      }, 0);
    });
  }

  ngOnDestroy(): void {
    const input = this.nativeInput;
    if (!input) return;
    input.removeEventListener('focus', this.onFocus);
    input.removeEventListener('blur', this.onBlur);
    input.removeEventListener('keydown', this.onKeydown);
  }

  private readonly onFocus = (): void => {
    const raw = this._sanitize(this.control?.value);
    this._setNativeValue(raw !== null ? String(raw) : '');
  };

  private readonly onBlur = (): void => {
    const displayValue = this.nativeInput?.value ?? '';
    if (!displayValue) return;
    const formatted = this._format(displayValue);
    this._setNativeValue(formatted);
    this.zone.run(() => {
      this.control?.markAsTouched();
      this.control?.setValue(formatted);
    });
  };

  private readonly onKeydown = (ev: KeyboardEvent): void => {
    if (['e', 'E', '+', '-', '.'].includes(ev.key)) {
      ev.preventDefault();
    }
  };

  private _format(value: string | number | null | undefined): string {
    const num = this._sanitize(value);
    return num !== null ? `${num}%` : '';
  }

  private _sanitize(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = parseFloat(String(value).replace('%', '').trim());
    return isNaN(num) ? null : num;
  }

  private _setNativeValue(display: string): void {
    const input = this.nativeInput;
    if (input) input.value = display;
  }

  private get nativeInput(): HTMLInputElement | null {
    return this.el.nativeElement.querySelector('input');
  }
}
